import type { ReactNode } from "react";

import i18n from "~/i18n/i18n";

export interface GridEmptyStateContent {
	icon: string;
	message?: string;
	title: string;
}

export interface GridEmptyStateParams {
	action?: ReactNode;
	errorMessage?: string;
	filteredIcon?: string;
	filteredMessage?: string;
	filteredTitle?: string;
	icon?: string;
	isError?: boolean;
	message?: string;
	onRetry?: () => void;
	quickFilterEmptyState?: boolean;
	shouldCoverRows?: boolean;
	title?: string;
}

interface GridLoadingInput {
	hasRows: boolean;
	isFetching: boolean;
}

const noDataState = (): GridEmptyStateContent => ({
	icon: "inbox",
	message: i18n.t("common:grid.emptyState.noData.message"),
	title: i18n.t("common:grid.emptyState.noData.title")
});

const filteredState = (): GridEmptyStateContent => ({
	icon: "filter_alt_off",
	message: i18n.t("common:grid.emptyState.filtered.message"),
	title: i18n.t("common:grid.emptyState.filtered.title")
});

const quickSearchState = (term: string): GridEmptyStateContent => ({
	icon: "search_off",
	message: i18n.t("common:grid.emptyState.quickSearch.message"),
	title: i18n.t("common:grid.emptyState.quickSearch.title", { term })
});

export const resolveGridEmptyState = (
	params: GridEmptyStateParams,
	isFilterActive: boolean,
	quickFilterText?: string
): GridEmptyStateContent => {
	const quickTerm = quickFilterText?.trim();
	if (quickTerm) {
		return quickSearchState(quickTerm);
	}
	if (isFilterActive) {
		const filtered = filteredState();
		return {
			icon: params.filteredIcon ?? filtered.icon,
			message: params.filteredMessage ?? filtered.message,
			title: params.filteredTitle ?? filtered.title
		};
	}
	const noData = noDataState();
	return {
		icon: params.icon ?? noData.icon,
		message: params.message ?? noData.message,
		title: params.title ?? noData.title
	};
};

export const computeGridLoading = ({ hasRows, isFetching }: GridLoadingInput): boolean => isFetching && !hasRows;
